import { useMemo } from 'react';

import { useMy } from '@/hooks/useMy';
import { useAuth } from '@/state/auth';
import { Setting } from '@/types';

/**
 * Hook to fetch customer display names for all tenants.
 * Uses the settings resource with name='display-name' to retrieve customer display names.
 */
export const useCustomerDisplayName = (props?: { enabled?: boolean }) => {
  const { whoami } = useAuth();
  const query = useMy(
    {
      resource: 'setting',
      allTenants: true,
    },
    {
      enabled: props?.enabled ?? true,
      doNotImpersonate: true, // Customer management should always run as the actual user
    }
  );

  const dataMap = useMemo(() => {
    // Filter for settings where name === 'display-name'
    const settings: Setting[] =
      query.data?.filter(item => item.name === 'display-name') || [];

    // Map by email (username field) to display name (value field)
    const map = new Map(
      settings.map(item => [
        item.username, // email address
        typeof item.value === 'string' ? item.value : item.username,
      ])
    );

    // The allTenants scan omits the caller's own partition; whoami loads it
    // explicitly, so merge it in (and let it win over any stale row).
    const callerEmail = whoami?.principal?.name;
    const callerDisplayName = whoami?.principal?.display_name;
    if (
      typeof callerEmail === 'string' &&
      callerEmail !== '' &&
      typeof callerDisplayName === 'string' &&
      callerDisplayName !== '' &&
      callerDisplayName !== callerEmail
    ) {
      map.set(callerEmail, callerDisplayName);
    }

    return map;
  }, [query.data, whoami]);

  return {
    ...query,
    dataMap,
  };
};
